import React, { useState, useEffect } from 'react';
import debounce from 'lodash/debounce';
import { HeroBanner } from '../../components/hero-banner';
import { Search } from '../../components/search';
import Footer from '../../components/footer';
import { Header } from '../../components/header';
import './search-results.less';
import { translatableText } from '../../utils/translatable-text';
import {
    SearchResultsDisplay,
    SearchResultCard
} from '../../components/search-results';
import {
    sentryCaptureEvent,
    sentryCaptureException
} from '../../utils/sentry/sentry-client';
import FilterSelector from './components/filter-selector';
import ResetButton from './components/reset-button';
import {
    Deployment,
    deploymentsArray as deployments,
    type FilterValue,
    dataCenterProducts,
    cloudProducts,
    dataCenterProductsIds,
    cloudProductsIds
} from './types';

interface SearchArticle {
    url: string;
    id: string;
    title: string;
    description: string;
    updatedDate: string;
    likes: number;
    views: number;
    products: {
        platform: string[];
    }[];
}

interface SearchResponse {
    items: SearchArticle[];
}

interface SearchQuery {
    searchTerm: string;
    deployment?: Deployment;
    productsQueryValue?: string[];
}

const queryHelpAggregator = async (
    query: SearchQuery,
    setResults: (results: SearchArticle[]) => void
) => {
    const helpAggregatorUrl = window.location.href.includes('localhost')
        ? 'https://support.dev.internal.atlassian.com/gateway/api/help-content/content/search'
        : '/gateway/api/help-content/content/search';

    try {
        const response = await fetch(helpAggregatorUrl, {
            body: JSON.stringify({
                query: query.searchTerm,
                filters: {
                    platform: query.deployment,
                    // TODO: since not all right products, default (all) has limited results
                    product: query.productsQueryValue
                }
            }),
            headers: {
                Accept: '*/*',
                'Content-Type': 'application/json',
                'Sec-Fetch-Mode': 'cors',
                'Sec-Fetch-Site': 'same-origin',
                authority: 'support.dev.internal.atlassian.com'
            },
            method: 'POST'
        });

        const json = await response.json();

        if (!response.ok) {
            sentryCaptureEvent({
                event: {
                    message: json.error,
                    level: 'error',
                    contexts: {
                        response: {
                            status_code: response.status
                        }
                    },
                    extra: {
                        json
                    }
                }
            });

            setResults([]);
            return;
        }

        const searchResults =
            (json as SearchResponse)?.items?.filter((items) => items) || [];
        // eslint-disable-next-line no-console
        console.log('searchResults', searchResults);
        setResults(searchResults);
    } catch (error) {
        sentryCaptureException({
            exception: error
        });
    }
};

const debouncedQueryHelpAggregator = debounce(queryHelpAggregator, 500);

export const SearchResultsPage = (entry: any) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState<SearchArticle[]>([]);
    const [deployment, setDeployment] = useState<Deployment>(Deployment.CLOUD);
    const [shouldReset, setShouldReset] = useState(false);

    // Product Filter Values
    const [productsQueryValue, setProductsQueryValue] =
        useState<string[]>(cloudProductsIds);
    const [listOfProducts, setListOfProducts] =
        useState<FilterValue[]>(cloudProducts);
    const [resetProductTitle, setResetProductTitle] = useState<boolean>(false);

    // Reset the product filter title and values when the deployment filter changes
    useEffect(() => {
        if (deployment === Deployment.CLOUD) {
            setResetProductTitle(true);
            setListOfProducts(cloudProducts);
            setProductsQueryValue(cloudProductsIds);
        } else {
            setResetProductTitle(true);
            setProductsQueryValue(dataCenterProductsIds);
            setListOfProducts(dataCenterProducts);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deployment]);

    const handleSubmit = (searchValue: string) => {
        // keep the search value and url in sync
        const url = new URL(window.location.href);
        url.searchParams.set('searchTerm', searchValue);
        window.history.replaceState({}, '', url);
        setSearchTerm(searchValue);

        debouncedQueryHelpAggregator(
            {
                searchTerm: searchValue,
                deployment,
                productsQueryValue
            },
            setResults
        );
    };

    useEffect(() => {
        debouncedQueryHelpAggregator(
            {
                searchTerm,
                deployment,
                productsQueryValue
            },
            setResults
        );
    }, [deployment, searchTerm, productsQueryValue]);

    // initialize the query from the url for when users first visit
    useEffect(() => {
        const parsedUrl = new URL(window.location.href);
        const searchTermFromUrl = parsedUrl.searchParams.get('searchTerm');
        setSearchTerm(searchTermFromUrl || '');
    }, []);

    return (
        <div className="content-wrapper">
            <Header
                id={entry.header.id}
                pageId={entry.id}
                resources={entry.resourceCards}
                additionalLinks={entry.header.additionalLinks}
                atlassianLogo={entry.header.atlassianLogo}
                logo={entry.header.atlassianSupportLogo.url}
                showPreviewBanner={entry.preview}
                atlassianSupportLogo={entry.header.atlassianSupportLogo}
                serverProducts={entry.header.serverProducts}
                cloudProducts={entry.header.cloudProducts}
            />
            <main>
                <div data-testid="search-results-page">
                    <HeroBanner
                        searchField={
                            <Search
                                onSearch={handleSubmit}
                                placeholder={
                                    translatableText.searchPlaceholder.text
                                }
                                initialValue={searchTerm}
                                analyticsMetadata={{
                                    parentComponent: 'search-results'
                                }}
                            />
                        }
                    />
                    <div className="filters-container">
                        <FilterSelector
                            filterTitle="Product"
                            filterValues={listOfProducts}
                            setQueryValues={setProductsQueryValue}
                            shouldReset={shouldReset}
                            setShouldReset={setShouldReset}
                            titleReset={resetProductTitle}
                            setTitleReset={setResetProductTitle}
                        />
                        <FilterSelector
                            filterTitle="Deployment"
                            filterValues={deployments}
                            setQueryValues={setDeployment}
                            allowFilterAll={false}
                            queryByString={true}
                            shouldReset={shouldReset}
                            setShouldReset={setShouldReset}
                        />
                        <ResetButton
                            onReset={() => {
                                setShouldReset(true);
                                setDeployment(Deployment.CLOUD);
                                setProductsQueryValue(cloudProductsIds);
                                setListOfProducts(cloudProducts);
                            }}
                        />
                    </div>
                    <SearchResultsDisplay>
                        {results?.map((result: any, index: number) => (
                            <SearchResultCard
                                key={result.id}
                                id={result.id}
                                title={result.title}
                                description={result.description}
                                href={result.url}
                                dateModified={result.updatedDate}
                                markedUseful={result.likes}
                                views={result.views}
                                platform={result.products?.[0]?.platform?.[0]}
                                searchTerm={searchTerm}
                                position={index + 1}
                            />
                        ))}
                    </SearchResultsDisplay>
                </div>
            </main>
            <Footer
                logo={entry.header.atlassianLogo.url}
                className={'margin-top-large'}
            />
        </div>
    );
};
